import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { persistAuthForNewTab } from "../../../utils/authStorage";
import "../Home/Homepage.css";
import "./SupportManagementPage.css";

const MAX_LENGTH = 50;
const DEFAULT_PAGE_LIMIT = 100;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "N/A";
  }
};

const truncateText = (text, max) => {
  if (!text) return "-----";
  if (text.length <= max) return text;
  return text.substring(0, max) + "...";
};

const getUserUuid = (row) =>
  row?.userData?.uuid ?? row?.userId ?? row?.uuid ?? "-----";

const getUserEmail = (row) =>
  row?.userData?.emailId ?? row?.emailId ?? row?.email ?? "-----";

const countByStatus = (list, status) =>
  list.filter((t) => (t.status || "").toLowerCase() === status).length;

const SupportManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [allTicketsForStats, setAllTicketsForStats] = useState([]);
  const [modalContent, setModalContent] = useState({ type: "", text: "", isOpen: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatingTicketId, setUpdatingTicketId] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [serverPaginated, setServerPaginated] = useState(true);
  const [summaryCounts, setSummaryCounts] = useState(null);

  const fetchTickets = useCallback(async (pageNum = page, limitNum = pageLimit) => {
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getAllSupportTickets({ page: pageNum, limit: limitNum });
      if (res?.success) {
        const list = Array.isArray(res?.data)
          ? res.data.filter((t) => t && typeof t === "object")
          : [];
        const pag = res?.pagination;
        const hasServerPagination =
          pag != null &&
          (pag.total_pages != null || pag.total != null || pag.totalPages != null);

        const summary = res?.summary ?? res?.status_summary ?? res?.counts ?? null;
        setSummaryCounts(summary && typeof summary === "object" ? summary : null);

        if (hasServerPagination) {
          setServerPaginated(true);
          setTickets(list);
          setAllTicketsForStats([]);
          const total = Number(pag.total ?? pag.totalCount ?? list.length) || 0;
          const pages =
            Number(pag.total_pages ?? pag.totalPages) ||
            Math.max(1, Math.ceil(total / limitNum));
          setTotalCount(total);
          setTotalPages(Math.max(1, pages));
        } else {
          setServerPaginated(false);
          setAllTicketsForStats(list);
          const total = list.length;
          const pages = Math.max(1, Math.ceil(total / limitNum));
          const start = (pageNum - 1) * limitNum;
          setTickets(list.slice(start, start + limitNum));
          setTotalCount(total);
          setTotalPages(pages);
        }
      } else {
        setError(res?.message || "Failed to fetch support tickets.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to fetch support tickets.");
    } finally {
      setLoading(false);
    }
  }, [page, pageLimit]);

  useEffect(() => {
    fetchTickets(page, pageLimit);
  }, [page, pageLimit, fetchTickets]);

  useEffect(() => {
    const u = location.state?.updatedTicket;
    if (u?._id) {
      setTickets((prev) => prev.map((t) => (t._id === u._id ? { ...t, status: u.status } : t)));
      setAllTicketsForStats((prev) =>
        prev.map((t) => (t._id === u._id ? { ...t, status: u.status } : t))
      );
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const counts = useMemo(() => {
    if (summaryCounts) {
      return {
        total: Number(summaryCounts.total ?? totalCount) || 0,
        open: Number(summaryCounts.open ?? summaryCounts.open_count) || 0,
        resolved: Number(summaryCounts.resolved ?? summaryCounts.resolved_count) || 0,
        closed: Number(summaryCounts.closed ?? summaryCounts.closed_count) || 0,
      };
    }

    const source = allTicketsForStats.length ? allTicketsForStats : tickets;
    return {
      total: serverPaginated ? totalCount : source.length,
      open: countByStatus(source, "open"),
      resolved: countByStatus(source, "resolved"),
      closed: countByStatus(source, "closed"),
    };
  }, [summaryCounts, allTicketsForStats, tickets, totalCount, serverPaginated]);

  const pageStart = totalCount === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = Math.min(page * pageLimit, totalCount);

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingTicketId(id);
    setError("");
    try {
      const res = await AdminService.updateSupportTicketStatus(id, newStatus);
      if (res?.success) {
        const updateRow = (t) => (t._id === id ? { ...t, status: newStatus, seen: 1 } : t);
        setTickets((prev) => prev.map(updateRow));
        setAllTicketsForStats((prev) => prev.map(updateRow));
      } else {
        setError(res?.message || "Failed to update ticket status.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to update ticket status.");
    } finally {
      setUpdatingTicketId("");
    }
  };

  const handleReadMore = (type, fullText) => {
    setModalContent({ type: type === "subject" ? "Subject" : "Description", text: fullText || "-----", isOpen: true });
  };

  const handleChat = (row) => {
    const ticketId = row._id || row.id;
    if (!ticketId) return;
    persistAuthForNewTab();
    localStorage.setItem(`supportTicket_${ticketId}`, JSON.stringify(row));
    const chatUrl = `${window.location.origin}/dashboard/support-message/${ticketId}`;
    window.open(chatUrl, "_blank");
  };

  const exportCSV = () => {
    const exportRows = allTicketsForStats.length ? allTicketsForStats : tickets;
    const headers = ["Date", "User Id", "Email Id", "Subject", "Status", "Description"];
    const rows = exportRows.map((t) => [
      formatDateTime(t.createdAt),
      getUserUuid(t) === "-----" ? "" : getUserUuid(t),
      getUserEmail(t) === "-----" ? "" : getUserEmail(t),
      t.subject || "",
      t.status || "",
      t.description || "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "support_tickets.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <main className="dashboard-main dashboard-subpage support-management-page">
      <section className="dashboard-hero support-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Support</span>
          </p>
          <h1 className="dashboard-hero__title">Support Management</h1>
          <p className="dashboard-hero__subtitle">Manage customer support tickets and queries</p>
        </div>
        <div className="support-hero__icon">
          <i className="fas fa-headset" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="support-stats">
          <div className="support-stat-card">
            <span className="support-stat-card__value">{counts.total}</span>
            <span className="support-stat-card__label">Total Tickets</span>
          </div>
          <div className="support-stat-card support-stat-card--open">
            <span className="support-stat-card__value">{counts.open}</span>
            <span className="support-stat-card__label">Open</span>
          </div>
          <div className="support-stat-card support-stat-card--resolved">
            <span className="support-stat-card__value">{counts.resolved}</span>
            <span className="support-stat-card__label">Resolved</span>
          </div>
          <div className="support-stat-card support-stat-card--closed">
            <span className="support-stat-card__value">{counts.closed}</span>
            <span className="support-stat-card__label">Closed</span>
          </div>
        </div>

        <section className="support-list-section">
          <div className="support-list-header">
            <h2 className="support-list__title">
              <i className="fas fa-list-alt" /> Issue List ({totalCount})
            </h2>
            <div className="support-list-header__actions">
              <label className="support-page-limit">
                <span>Rows per page</span>
                <select
                  value={pageLimit}
                  onChange={handlePageLimitChange}
                  className="support-page-limit__select"
                  disabled={loading}
                >
                  {PAGE_LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              {totalCount > 0 && (
                <div className="support-export-wrap">
                  <button type="button" className="support-btn-export" onClick={exportCSV}>
                    <i className="fas fa-download" /> Export
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="support-table-wrap">
            {loading ? (
              <div className="support-empty"><i className="fas fa-spinner fa-spin" /><p>Loading support tickets...</p></div>
            ) : error ? (
              <div className="support-empty"><i className="fas fa-exclamation-circle" /><p>{error}</p></div>
            ) : tickets.length === 0 ? (
              <div className="support-empty"><i className="fas fa-headset" /><p>No support tickets found</p></div>
            ) : (
              <table className="support-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Issue Image</th>
                    <th>User Id</th>
                    <th>Email Id</th>
                    <th>Subject</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((row) => {
                    const subj = row.subject || "-----";
                    const desc = row.description || "-----";
                    const subjLong = subj.length > MAX_LENGTH;
                    const descLong = desc.length > MAX_LENGTH;
                    return (
                      <tr key={row._id || row.id}>
                        <td>{formatDateTime(row.createdAt)}</td>
                        <td>{row.issueImage ? <a href={row.issueImage} target="_blank" rel="noreferrer" className="support-img-link">View</a> : <span className="support-no-image">No Image</span>}</td>
                        <td><span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{getUserUuid(row)}</span></td>
                        <td><span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{getUserEmail(row)}</span></td>
                        <td className="support-text-cell">
                          <span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{truncateText(subj, MAX_LENGTH)}</span>
                          {subjLong && <button type="button" className="support-read-more" onClick={(e) => { e.stopPropagation(); handleReadMore("subject", subj); }}>Read More</button>}
                        </td>
                        <td className="support-text-cell">
                          <span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{truncateText(desc, MAX_LENGTH)}</span>
                          {descLong && <button type="button" className="support-read-more" onClick={(e) => { e.stopPropagation(); handleReadMore("description", desc); }}>Read More</button>}
                        </td>
                        <td>
                          <span className={`support-status-badge support-status-badge--${(row.status || "").toLowerCase()}`}>
                            {row.status}{row.seen === 1 && <i className="fas fa-circle support-status-dot" />}
                          </span>
                        </td>
                        <td>
                          <div className="support-actions">
                            {(row.status || "").toLowerCase() === "open" ? (
                              <>
                                <button type="button" className="support-action-btn support-action-btn--close" onClick={() => handleStatusChange(row._id, "Closed")} disabled={updatingTicketId === row._id}><i className={`fas ${updatingTicketId === row._id ? "fa-spinner fa-spin" : "fa-times-circle"}`} /> Close</button>
                                <button type="button" className="support-action-btn support-action-btn--resolve" onClick={() => handleStatusChange(row._id, "Resolved")} disabled={updatingTicketId === row._id}><i className={`fas ${updatingTicketId === row._id ? "fa-spinner fa-spin" : "fa-check-circle"}`} /> Resolve</button>
                                <button type="button" className="support-action-btn support-action-btn--chat" onClick={() => handleChat(row)}><i className="fas fa-comments" /> Chat</button>
                              </>
                            ) : (
                              <div className="support-actions-closed">
                                <span className={`support-actions-status-text support-actions-status-text--${(row.status || "").toLowerCase()}`}>{row.status}</span>
                                <button type="button" className="support-action-btn support-action-btn--chat" onClick={() => handleChat(row)}><i className="fas fa-comments" /> Chat</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {!loading && totalCount > 0 && (
            <div className="support-pagination">
              <span className="support-pagination__info">
                Showing {pageStart}–{pageEnd} of {totalCount}
                {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
              </span>
              <div className="support-pagination__buttons">
                <button
                  type="button"
                  className="support-pagination__btn"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <i className="fas fa-chevron-left" /> Previous
                </button>
                <button
                  type="button"
                  className="support-pagination__btn"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next <i className="fas fa-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {modalContent.isOpen && (
        <div className="support-modal-overlay" onClick={() => setModalContent((m) => ({ ...m, isOpen: false }))}>
          <div className="support-modal" onClick={(e) => e.stopPropagation()}>
            <div className="support-modal-header">
              <h3 className="support-modal-title">Full {modalContent.type}</h3>
              <button type="button" className="support-modal-close" onClick={() => setModalContent((m) => ({ ...m, isOpen: false }))}><i className="fas fa-times" /></button>
            </div>
            <div className="support-modal-body"><p className="support-modal-text">{modalContent.text}</p></div>
            <div className="support-modal-footer">
              <button type="button" className="support-modal-btn-close" onClick={() => setModalContent((m) => ({ ...m, isOpen: false }))}>Close</button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
};

export default SupportManagementPage;
